import React, { useState } from 'react';
import {
  Box, Typography, TextField, IconButton, Chip, Tooltip, Collapse,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReplayIcon from '@mui/icons-material/Replay';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import MicIcon from '@mui/icons-material/Mic';

// 將 {{variables.xxx}} 和 {{slots.xxx}} 轉換為顯示用的 chip 文字
const VARIABLE_DISPLAY = {
  // 流程變數
  'variables.restaurant_name': '店名',
  'variables.company_name': '公司名稱',
  'variables.max_party_size': '最大人數',
  // 主要收集欄位
  'slots.name': '姓名',
  'slots.date': '日期',
  'slots.time': '時間',
  'slots.phone': '電話',
  'slots.adults': '大人人數',
  'slots.children': '小孩人數',
  'slots.address': '地址',
  'slots.issue_description': '問題描述',
  'slots.duck': '烤鴨',
  'slots.kid_chairs': '兒童椅',
  // 查詢結果
  'slots.search_date': '日期',
  'slots.search_time': '時間',
  'slots.search_name': '姓名',
  'slots.search_party': '人數',
  'slots.search_date_1': '日期',
  'slots.search_time_1': '時間',
  'slots.search_name_1': '姓名',
  'slots.search_party_1': '人數',
  'slots.search_date_2': '日期',
  'slots.search_time_2': '時間',
  'slots.search_party_2': '人數',
  'slots.search_date_3': '日期',
  'slots.search_time_3': '時間',
  'slots.search_party_3': '人數',
  // 取消/修改
  'slots.cancel_date': '日期',
  'slots.cancel_time': '時間',
  'slots.cancel_name': '姓名',
  'slots.cancel_party': '人數',
  'slots.modify_orig_date': '原日期',
  'slots.modify_orig_time': '原時間',
  'slots.modify_orig_name': '姓名',
  'slots.modify_orig_adults': '原大人數',
  'slots.modify_orig_children': '原小孩數',
  'slots.new_date': '新日期',
  'slots.new_time': '新時間',
  'slots.new_name': '新姓名',
  'slots.new_adults': '新大人數',
  'slots.new_phone': '新電話',
  'slots.new_duck': '新烤鴨',
  // 外撥提醒回應
  'slots.reminder_response': '備註資訊',
  'slots.party_count': '總人數',
  // 計算值
  'computed.confirmation_text': '確認摘要',
  'computed.booking_summary': '訂位摘要',
};

// slot_name → 中文標籤
const SLOT_LABEL = {
  name: '姓名', date: '日期', time: '時間', phone: '電話',
  adults: '大人人數', children: '小孩人數', address: '地址',
  issue_description: '問題描述', duck: '烤鴨', kid_chairs: '兒童椅',
  gender: '性別',
  // 外撥提醒回應 + 修改訂位新值
  reminder_response: '備註資訊',
  new_date: '新日期', new_time: '新時間', new_adults: '新人數',
  new_phone: '新電話', new_name: '新姓名',
  party_count: '總人數',
};

// 將模板變數替換為可讀的 inline chip（顯示用）
const renderTextWithVariables = (text) => {
  if (!text) return '';
  const parts = text.split(/({{[^}]+}})/g);
  return parts.map((part, i) => {
    const match = part.match(/^{{(.+)}}$/);
    if (match) {
      const varKey = match[1].trim();
      const label = VARIABLE_DISPLAY[varKey] || varKey.split('.').pop();
      return (
        <Box
          component="span"
          key={i}
          sx={{
            display: 'inline-flex', alignItems: 'center',
            px: 0.8, mx: 0.2, borderRadius: '4px',
            bgcolor: '#e8f4fd', color: '#145da0',
            fontSize: '0.85em', fontWeight: 500,
            border: '1px solid #b8d8f0', whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
        >
          {label}
        </Box>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const ScriptBubble = ({
  step, onTextChange, onPlayAudio, onStopAudio, readOnly = false,
  isRegenerating = false, onRegenerateAudio
}) => {
  // 文字是否為靜態（無 {{template}}）— 只有靜態文字會觸發 TTS 自動重生
  const isStaticText = (text) => text && text.trim() && !/\{\{.*?\}\}/.test(text);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [showRetry, setShowRetry] = useState(false);
  const [editingRetryIdx, setEditingRetryIdx] = useState(-1);
  const [retryEditText, setRetryEditText] = useState('');
  const [playing, setPlaying] = useState(false);

  const isAi = step.role === 'ai' || step.role === 'system';
  const hasRetry = step.retryTexts && step.retryTexts.length > 0;

  const handleEditStart = () => {
    setEditText(step.text);
    setEditing(true);
  };

  const handleEditSave = () => {
    if (editText !== step.text) {
      const field = step.type === 'collect_slot' ? 'prompt_text'
        : step.type === 'api_call' ? 'pre_play_text'
        : 'text';
      onTextChange(step.nodeId, field, editText);
      // 自動產生語音 — 只有當文字是靜態（無 {{template}}）才呼叫 TTS
      if (isStaticText(editText) && onRegenerateAudio) {
        onRegenerateAudio(step.nodeId, field, editText);
      }
    }
    setEditing(false);
  };

  const handleEditCancel = () => {
    setEditing(false);
  };

  const handleRetryEditStart = (idx) => {
    setRetryEditText(step.retryTexts[idx]);
    setEditingRetryIdx(idx);
  };

  const handleRetryEditSave = () => {
    if (editingRetryIdx >= 0 && retryEditText !== step.retryTexts[editingRetryIdx]) {
      const newRetryTexts = [...step.retryTexts];
      newRetryTexts[editingRetryIdx] = retryEditText;
      onTextChange(step.nodeId, 'retry_texts', newRetryTexts);
    }
    setEditingRetryIdx(-1);
  };

  // 播放語音預覽
  const handlePlay = () => {
    if (playing) {
      if (onStopAudio) onStopAudio();
      setPlaying(false);
      return;
    }
    if (onPlayAudio) {
      onPlayAudio(step, (isPlaying) => {
        setPlaying(isPlaying);
      });
    }
  };

  // 步驟標題 + 語音收集/條件標籤
  const getStepLabel = () => {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: '#bbb', fontSize: '11px' }}>
          {step.title || ''}
        </Typography>
        {step.isCollect && (
          <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: '2px',
            fontSize: '10px', color: '#145da0', bgcolor: '#e8f4fd',
            px: 0.6, py: '1px', borderRadius: '3px',
          }}>
            <MicIcon sx={{ fontSize: 10 }} /> 語音收集
          </Box>
        )}
        {step.conditionContext && (
          <Box component="span" sx={{
            fontSize: '10px', color: '#e67e22', bgcolor: '#fff3e0',
            px: 0.6, py: '1px', borderRadius: '3px',
          }}>
            {step.conditionContext}
          </Box>
        )}
      </Box>
    );
  };

  // 通話結束 → 渲染為輕量結束標記（不顯示完整 AI 氣泡，避免重複道別）
  if (step.type === 'end_call') {
    const farewell = step.text || '';
    return (
      <Box
        sx={{
          my: 2, mx: 1, px: 2, py: 1,
          borderRadius: '8px',
          border: '1px dashed #e0e0e0',
          bgcolor: '#fafafa',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 1, color: '#888', fontSize: '13px',
        }}
      >
        <Box component="span" sx={{ fontSize: '15px' }}>🔚</Box>
        <Box component="span" sx={{ fontWeight: 500 }}>{step.title || '結束通話'}</Box>
        {farewell && (
          <Box component="span" sx={{ color: '#aaa', fontStyle: 'italic' }}>
            ・{farewell}
          </Box>
        )}
      </Box>
    );
  }

  // API 呼叫 → 渲染為系統分隔線
  if (step.type === 'api_call') {
    // 將 title 轉為友善的系統動作描述
    const ACTION_LABELS = {
      '確認座位': '系統查詢座位資訊',
      '送出訂位': '系統建立訂位',
      '查詢訂位': '系統查詢訂位資料',
      '查詢FAQ': '系統查詢 FAQ',
      '掃描四個時段': '系統查詢其他時段',
      '取消-查詢訂位': '系統查詢訂位資料',
      '取消-執行刪除': '系統取消訂位',
      '修改-查詢訂位': '系統查詢訂位資料',
      '修改-送出': '系統修改訂位',
    };
    const label = ACTION_LABELS[step.title] || `系統${step.title || '處理中'}`;

    return (
      <Box
        sx={{
          my: 2, mx: 1, px: 2, py: 1,
          borderRadius: '8px',
          border: '1px dashed #b8d8f0',
          bgcolor: '#f0f8ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 0.5, color: '#145da0', fontSize: '13px', fontWeight: 500,
        }}
      >
        <Box component="span" sx={{ fontSize: '16px' }}>⏳</Box>
        {label}
      </Box>
    );
  }

  // 氣泡樣式
  const bubbleStyles = isAi ? {
    bgcolor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    borderTopLeftRadius: '4px',
    '&:hover': { borderColor: '#145da0', boxShadow: '0 2px 8px rgba(20,93,160,.1)' },
  } : {
    bgcolor: '#f0e6ff',
    border: '1px solid #ddd0f0',
    borderRadius: '12px',
    borderTopRightRadius: '4px',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isAi ? 'row' : 'row-reverse',
        gap: 1,
        mb: 1.5,
      }}
    >
      {/* 頭像 */}
      {isAi ? (
        <Box
          component="img"
          src="https://d3uyjtuwlqw6e9.cloudfront.net/static/ai_avatar/ai_avatar_1.png"
          alt="AI"
          sx={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            objectFit: 'cover',
          }}
        />
      ) : (
        <Box
          sx={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '15px', bgcolor: '#f0e6ff',
          }}
        >
          👤
        </Box>
      )}

      {/* 氣泡內容 — 編輯模式時展開全寬 */}
      <Box sx={{ maxWidth: editing ? '95%' : '72%', minWidth: editing ? '60%' : 0, transition: 'max-width 0.2s' }}>
        {/* 步驟標籤列 */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3,
          textAlign: isAi ? 'left' : 'right',
          justifyContent: isAi ? 'flex-start' : 'flex-end',
        }}>
          {getStepLabel()}
        </Box>

        {/* 氣泡本體 */}
        <Box
          sx={{
            ...bubbleStyles,
            px: 2,
            py: 1.2,
            cursor: step.editable && !readOnly ? 'pointer' : 'default',
            transition: '0.2s',
            '&:hover .edit-btn': { opacity: 1 },
          }}
          onClick={() => {
            if (step.editable && !readOnly && !editing) handleEditStart();
          }}
        >
          {/* 主文字 */}
          {editing ? (
            <Box onClick={(e) => e.stopPropagation()}>
              <TextField
                multiline
                fullWidth
                size="small"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoFocus
                sx={{
                  '& .MuiInputBase-root': { fontSize: '0.9rem' },
                  '& .MuiOutlinedInput-root': { borderColor: '#145da0' },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                <IconButton size="small" onClick={handleEditSave} color="primary">
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleEditCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '14px' }}
            >
              {renderTextWithVariables(step.text)}
            </Typography>
          )}
        </Box>

        {/* 播放語音按鈕 + 語音產生中 spinner */}
        {isAi && step.text && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Box
              onClick={handlePlay}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.3,
                borderRadius: '12px', fontSize: '11px', cursor: 'pointer',
                bgcolor: playing ? '#145da0' : '#f0f0f0',
                color: playing ? '#fff' : '#666',
                border: `1px solid ${playing ? '#145da0' : '#ddd'}`,
                transition: '0.2s',
                '&:hover': { bgcolor: playing ? '#0e4a80' : '#e8f4fd', borderColor: '#145da0', color: playing ? '#fff' : '#145da0' },
              }}
            >
              {playing ? <StopIcon sx={{ fontSize: 14 }} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
              {playing ? '停止' : '播放語音'}
            </Box>
            {isRegenerating && (
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                px: 0.8, py: 0.2,
                borderRadius: '10px', fontSize: '10px',
                color: '#145da0',
              }}>
                <CircularProgress size={10} /> 語音產生中…
              </Box>
            )}
          </Box>
        )}

        {/* Retry 文字 */}
        {hasRetry && (
          <Box sx={{ mt: 1 }}>
            <Box
              onClick={() => setShowRetry(!showRetry)}
              sx={{
                display: 'flex', alignItems: 'center', cursor: 'pointer',
                color: '#e67e22', '&:hover': { color: '#d35400' },
              }}
            >
              <ReplayIcon sx={{ fontSize: 14, mr: 0.3 }} />
              <Typography variant="caption" sx={{ color: 'inherit', fontSize: '11px' }}>
                重試語句 ({step.retryTexts.length})
              </Typography>
              {showRetry ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            </Box>

            <Collapse in={showRetry}>
              <Box sx={{ mt: 0.5 }}>
                {step.retryTexts.map((rt, idx) => (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    {editingRetryIdx === idx ? (
                      <Box>
                        <TextField
                          multiline
                          fullWidth
                          size="small"
                          value={retryEditText}
                          onChange={(e) => setRetryEditText(e.target.value)}
                          autoFocus
                          sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.3 }}>
                          <IconButton size="small" onClick={handleRetryEditSave} color="primary">
                            <CheckIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => setEditingRetryIdx(-1)}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Box
                          onClick={() => { if (!readOnly) handleRetryEditStart(idx); }}
                          sx={{
                            px: 1.5, py: 0.8,
                            borderRadius: '8px', fontSize: '12px', lineHeight: 1.5,
                            bgcolor: '#fff8f0', border: '1px dashed #f0c080', color: '#8a6d3b',
                            cursor: readOnly ? 'default' : 'pointer',
                            '&:hover': readOnly ? {} : { borderColor: '#e67e22' },
                          }}
                        >
                          {renderTextWithVariables(rt)}
                        </Box>
                        {/* 每條重試語句的播放按鈕 */}
                        {step.retryAudios && step.retryAudios[idx] && (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onPlayAudio) {
                                onPlayAudio({ ...step, audioAsset: step.retryAudios[idx], text: rt }, () => {});
                              }
                            }}
                            sx={{
                              display: 'inline-flex', alignItems: 'center', gap: '3px',
                              mt: 0.3, px: 0.8, py: '2px',
                              borderRadius: '10px', fontSize: '10px', cursor: 'pointer',
                              bgcolor: '#fff3e0', border: '1px solid #f0c080', color: '#8a6d3b',
                              '&:hover': { bgcolor: '#e67e22', color: '#fff', borderColor: '#e67e22' },
                            }}
                          >
                            <PlayArrowIcon sx={{ fontSize: 12 }} /> 播放
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ScriptBubble;
